import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Layers, Plus, Search, Trash2, X, Tag } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function CategoriesPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/products/categories', { search }],
    queryFn: () => api.get('/products/categories', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newCat: typeof form) => api.post('/products/categories', newCat),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/categories'] })
      toast.success('دسته‌بندی جدید اضافه شد')
      setIsOpen(false)
      setForm({ name: '', slug: '', description: '' })
    },
    onError: () => toast.error('خطا در ایجاد دسته‌بندی')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/products/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/categories'] })
      toast.success('دسته‌بندی حذف شد')
    },
    onError: () => toast.error('خطا در حذف دسته‌بندی')
  })

  const items = data?.items || []

  return (
    <div className="p-4 space-y-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">دسته‌بندی محصولات</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">سازماندهی محصولات در گروه‌ها و دسته‌های مختلف</p>
        </div>
        <Button onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 ml-1" />
          دسته‌بندی جدید
        </Button>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input placeholder="جستجوی دسته‌بندی..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-5 w-48" /></div>)}</div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Layers className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">دسته‌بندی یافت نشد</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">نام دسته</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">شناسه (Slug)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">توضیحات</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary-500" />
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{item.slug || '---'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">{item.description || '---'}</td>
                    <td className="px-4 py-3 text-sm text-left">
                      <button
                        onClick={() => deleteMutation.mutate(item.id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal create category */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-primary-500" />
                  افزودن دسته‌بندی جدید
                </h3>
                <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.name) {
                    toast.error('نام دسته‌بندی الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام دسته‌بندی</label>
                  <Input
                    placeholder="مثلا: کالای دیجیتال"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">شناسه انگلیسی (Slug)</label>
                  <Input
                    placeholder="digital"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">توضیحات</label>
                  <Input
                    placeholder="توضیح کوتاه..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ذخیره دسته‌بندی
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
